// ----------------------------------------------------------------
// <copyright company='Microsoft Corporation'>
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
// ----------------------------------------------------------------

import userTierConfig from './userTiersConfig.json';
import { saveLoadFactory, SaveLocation } from 'application-storage';
const agentTier = saveLoadFactory('agentSelectedTier', SaveLocation.LocalStorage);

export interface UserTierConfig {
    accountType: string;
    alternateTiers: [];
    commercialRegistration: boolean;
    defaultToStoreAddress: boolean;
    description: string;
    deviceAndWarrantyCheck: boolean;
    orderStatus: boolean;
    showReplaceDevice: boolean;
    showReplaceAccessory: boolean;
    roboChannelName: string;
    showCancelOrder: boolean;
    type: string;
    isExternalTier: boolean;
    showSafetySection: boolean;
}

function addHelperMethods(tierConfig: UserTierConfig) {
    const checkForAlternateTier = (tierToCheck?: number | string | null) => {
        return tierToCheck && tierConfig.alternateTiers.some((tier) => {
            // tslint:disable-next-line: triple-equals - matching number or string
            return tier == tierToCheck;
        });
    };

    return {
        ...tierConfig,
        checkForAlternateTier,
    };
}

export function isExternalTier(agentTier: string) {
    const tierConfig = getUserTierConfig(agentTier);
    return (tierConfig.isExternalTier);
}

export function getUserTierConfig(tierName?: number | string | null) {
    const tier = tierName || agentTier.getValue();
    return addHelperMethods({ ...userTierConfig.default, ...userTierConfig[tier] });
}

export function getTierName() {
    return agentTier.getValue();
}

export function setTierName(tierName: string) {
    agentTier.setValue(tierName);
    return getUserTierConfig();
}

export function getTierCategoryByName(tierCategory: string) {
    return userTierConfig.tierCategories.filter(x => x.categoryName.toLowerCase() === tierCategory.toLowerCase())[0];
}

export function checkIsUserEnterpriseCategoryFromTier(tierName: string) {
    const tier = parseInt(tierName, 10) || parseInt(agentTier.getValue(), 10);
    return userTierConfig.tierCategories.filter(x => x.categoryName.toLowerCase() === 'enterprisecategorytiers' && x.categoryValues.includes(tier)).length > 0;
}

export function checkIsOnSiteRepairFromTier(tierName: string) {
    const tier = parseInt(tierName, 10) || parseInt(agentTier.getValue(), 10);
    return userTierConfig.tierCategories.filter(x => x.categoryName === 'OnSiteRepairPartnerTier' && x.categoryValues.includes(tier)).length > 0;
}

export function checkIsSoftwareSupportEnabledFromTier(tierName: string) {
    const tier = parseInt(tierName, 10) || parseInt(agentTier.getValue(), 10);
    return userTierConfig.tierCategories.filter(x => x.categoryName === 'SoftwareSupportTier' && x.categoryValues.includes(tier)).length > 0;
}